import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { TabsProps } from 'antd';

import UbiquitiHostClientDevicesContainer from '@totem/components/ubiquiti/hosts/details/clientDevices/UbiquitiHostClientDevicesContainer';
import UbiquitiHostClientDevicesTable from '@totem/components/ubiquiti/hosts/details/clientDevices/UbiquitiHostClientDevicesTable';
import UbiquitiHostDevicesContainer from '@totem/components/ubiquiti/hosts/details/devices/UbiquitiHostDevicesContainer';
import UbiquitiHostDevicesTable from '@totem/components/ubiquiti/hosts/details/devices/UbiquitiHostDevicesTable';
import UbiquitiHostDnsPoliciesContainer from '@totem/components/ubiquiti/hosts/details/dnsPolicies/UbiquitiHostDnsPoliciesContainer';
import UbiquitiHostDnsPoliciesTable from '@totem/components/ubiquiti/hosts/details/dnsPolicies/UbiquitiHostDnsPoliciesTable';
import UbiquitiHostFirewallPoliciesContainer from '@totem/components/ubiquiti/hosts/details/firewallPolicies/UbiquitiHostFirewallPoliciesContainer';
import UbiquitiHostFirewallPoliciesTable from '@totem/components/ubiquiti/hosts/details/firewallPolicies/UbiquitiHostFirewallPoliciesTable';
import UbiquitiHostFirewallZonesContainer from '@totem/components/ubiquiti/hosts/details/firewallZones/UbiquitiHostFirewallZonesContainer';
import UbiquitiHostFirewallZonesTable from '@totem/components/ubiquiti/hosts/details/firewallZones/UbiquitiHostFirewallZonesTable';
import UbiquitiHostISPMetricsChart from '@totem/components/ubiquiti/hosts/details/ispMetrics/UbiquitiHostISPMetricsChart';
import UbiquitiHostISPMetricsContainer from '@totem/components/ubiquiti/hosts/details/ispMetrics/UbiquitiHostISPMetricsContainer';
import UbiquitiHostNetworksContainer from '@totem/components/ubiquiti/hosts/details/networks/UbiquitiHostNetworksContainer';
import UbiquitiHostNetworksTable from '@totem/components/ubiquiti/hosts/details/networks/UbiquitiHostNetworksTable';
import UbiquitiHostWifiBroadcastsContainer from '@totem/components/ubiquiti/hosts/details/wifiBroadcasts/UbiquitiHostWifiBroadcastsContainer';
import UbiquitiHostWifiBroadcastsTable from '@totem/components/ubiquiti/hosts/details/wifiBroadcasts/UbiquitiHostWifiBroadcastsTable';
import UbiquitiHostCard from '@totem/components/ubiquiti/hosts/UbiquitiHostCard';
import { UbiquitiHostGroup } from '@totem/components/ubiquiti/utilities';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';

type LocationState = {
  group?: UbiquitiHostGroup;
};

const UbiquitiHostDetailsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const group = (location.state as LocationState)?.group ?? null;

  const getTabItems = () => {
    const tabItems: TabsProps['items'] = [
      {
        key: 'devices',
        label: 'UniFi Devices',
        children: <UbiquitiHostDevicesTable />,
      },
      {
        key: 'clientDevices',
        label: 'Connected Clients',
        children: (
          <UbiquitiHostClientDevicesContainer hostId={id}>
            <UbiquitiHostClientDevicesTable />
          </UbiquitiHostClientDevicesContainer>
        ),
      },
      {
        key: 'wifiBroadcasts',
        label: 'WiFi Broadcasts',
        children: (
          <UbiquitiHostWifiBroadcastsContainer hostId={id}>
            <UbiquitiHostWifiBroadcastsTable />
          </UbiquitiHostWifiBroadcastsContainer>
        ),
      },
      {
        key: 'networks',
        label: 'Networks',
        children: <UbiquitiHostNetworksTable />,
      },
      {
        key: 'firewallPolicies',
        label: 'Firewall Policies',
        children: (
          <UbiquitiHostFirewallPoliciesContainer hostId={id}>
            <UbiquitiHostFirewallPoliciesTable />
          </UbiquitiHostFirewallPoliciesContainer>
        ),
      },
      {
        key: 'firewallZones',
        label: 'Firewall Zones',
        children: <UbiquitiHostFirewallZonesTable />,
      },
      {
        key: 'dnsPolicies',
        label: 'DNS Policies',
        children: (
          <UbiquitiHostDnsPoliciesContainer hostId={id}>
            <UbiquitiHostDnsPoliciesTable />
          </UbiquitiHostDnsPoliciesContainer>
        ),
      },
      {
        key: 'ispMetrics',
        label: 'ISP Metrics',
        children: (
          <UbiquitiHostISPMetricsContainer hostId={id}>
            <UbiquitiHostISPMetricsChart />
          </UbiquitiHostISPMetricsContainer>
        ),
      },
    ];
    return tabItems;
  };

  return (
    <ContentLayout>
      {group !== null ? (
        <UbiquitiHostCard group={group} />
      ) : (
        <IBCard size="small">
          Host summary is only available when opened from the{' '}
          <Link to="/dashboard/ubiquiti/hosts">Ubiquiti Hosts</Link> list.
        </IBCard>
      )}
      <br />
      <IBCard isCovered>
        <UbiquitiHostDevicesContainer hostId={id}>
          <UbiquitiHostNetworksContainer hostId={id}>
            <UbiquitiHostFirewallZonesContainer hostId={id}>
              <IBTabs defaultActiveKey="devices" items={getTabItems()} />
            </UbiquitiHostFirewallZonesContainer>
          </UbiquitiHostNetworksContainer>
        </UbiquitiHostDevicesContainer>
      </IBCard>
    </ContentLayout>
  );
};

export default UbiquitiHostDetailsPage;
